import { Component, ComponentRef, signal, ViewChild, ViewContainerRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ModalComponent } from '../../../shared/components/modal/modal.component';
import { ModalService } from '../../services/modal.service';
import { IModal } from '../../services/typings/modal';
import { AutoAnimateDirective } from '../../../shared/directives/auto-animate.directive';
import { ComponentWrapperComponent } from './component-wrapper/component-wrapper.component';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { BehaviorSubject, delay, distinctUntilChanged, filter, finalize, of, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import { NgIcon } from '@ng-icons/core';

interface IRenderedModal extends IModal {
    ref: ComponentRef<ComponentWrapperComponent>;
}

@Component({
    selector: 'app-modal-container',
    imports: [CommonModule, ModalComponent, AutoAnimateDirective, NgIcon],
    templateUrl: './modal-container.component.html',
    styleUrl: './modal-container.component.scss',
    animations: [
        trigger('fadeAnimation', [
            state('visible', style({
                opacity: 1,
            })),
            state('hidden', style({
                opacity: 0,
            })),
            transition('visible => hidden', [animate('100ms')]),
            transition('hidden => visible', [animate('150ms 150ms')]),
        ]),
    ],
})
export class ModalContainerComponent {

    private static readonly ANIMATION_DURATION = 150;

    private _stack: IRenderedModal[] = [];

    private readonly _state = new BehaviorSubject<'visible' | 'hidden'>('visible');
    public readonly state$ = this._state.asObservable().pipe(
        distinctUntilChanged(),
    );

    protected size = signal('3xl');

    // ---------- Refs ----------

    @ViewChild('container', { read: ViewContainerRef, static: false }) containerRef!: ViewContainerRef;

    constructor(private readonly service: ModalService) {
        service.pushed
            .pipe(
                map(modal => this.renderModal(modal)),
            )
            .subscribe(modal => this.onPushed(modal));

        service.removed
            .pipe(
                map(modal => this.findInStack(modal.id)),
                filter(Boolean),
            )
            .subscribe((modal) => this.onRemoved(modal));

        service.cleared.subscribe(() => this.onCleared());
    }

    // ---------- Event Handlers ----------

    protected onClose() {
        this.service.pop();
    }

    private onPushed(modal: IRenderedModal) {
        if (this.currentModal) {
            this.hideComponent(this.currentModal);
        }

        modal.onInit?.({
            component: modal.ref.instance.component,
            close: () => this.service.remove(modal.id),
            closeAll: () => this.service.clear(),
        });

        this.pushToStack(modal);
        this.show();

        this.size.set(this.currentModal.size ?? '3xl');
    }

    protected onRemoved(modal: IRenderedModal) {
        this.hide();
        this.removeFromStack(modal);

        of(null).pipe(
            delay(ModalContainerComponent.ANIMATION_DURATION),
            takeUntil(this.state$.pipe(filter(state => state === 'visible'))),
            finalize(() => {
                this.destroyComponent(modal);
                if (this.currentModal) {
                    this.showComponent(this.currentModal);
                }
            }),
        ).subscribe(() => this.show());

        if (this.currentModal) {
            this.size.set(this.currentModal.size ?? '3xl');
        }
    }

    private onCleared() {
        this._stack.forEach(modal => this.destroyComponent(modal));
        this._stack = [];
    }

    // ---------- Methods ----------

    protected goBack() {
        this.service.pop();
    }

    private show() {
        this._state.next('visible');
    }

    private hide() {
        this._state.next('hidden');
    }

    private findInStack(id: string) {
        return this._stack.find(modal => modal.id === id);
    }

    private renderModal(modal: IModal): IRenderedModal {
        const ref = this.containerRef.createComponent(ComponentWrapperComponent);
        ref.instance.createComponent(modal.component);
        return { ...modal, ref };
    }

    private destroyComponent(modal: IRenderedModal) {
        modal.ref.destroy();
    }

    private pushToStack(modal: IRenderedModal) {
        this._stack.push(modal);
    }

    private removeFromStack(modal: IRenderedModal) {
        const index = this._stack.indexOf(modal);
        this._stack.splice(index, 1);
    }

    private hideComponent(modal: IRenderedModal) {
        modal.ref.instance.show = false;
    }

    private showComponent(modal: IRenderedModal) {
        modal.ref.instance.show = true;
    }

    // ---------- Getters ----------

    get currentModal() {
        return this._stack[this._stack.length - 1];
    }

    get closable() {
        return (this.currentModal?.closeable ?? true);
    }

    get state() {
        return this._state.value;
    }

    get count() {
        return this._stack.length;
    }

}
